import React, { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
  pageName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/** Catches render errors in heavy pages (Portal, Booking) so a bad row does not white-screen the app. */
export class PageErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[PageErrorBoundary]", error, info.componentStack);
  }

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[50vh] flex flex-col items-center justify-center px-6 py-16 text-center">
          <div className="rounded-3xl border border-amber-200/80 bg-white/80 backdrop-blur-md shadow-lg p-10 max-w-md">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" aria-hidden />
            <h1 className="text-xl font-black text-slate-900 mb-2">
              Something went wrong{this.props.pageName ? ` — ${this.props.pageName}` : ""}
            </h1>
            <p className="text-sm text-slate-600 mb-6">
              Please refresh the page. If this keeps happening, contact support with what you were doing.
            </p>
            <button
              type="button"
              onClick={this.handleRefresh}
              className="w-full py-3.5 rounded-2xl bg-slate-900 text-white text-sm font-bold hover:bg-black transition-all duration-200 ease-in-out"
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
